"use client";

import { Badge } from "@faire/slate/components/badge";
import { Tag } from "@faire/slate/components/tag";
import { getSlots } from "@faire/slate/lib/getSlots";
import { mergeRefs } from "@faire/slate/lib/mergeRefs";
import { componentCls } from "@faire/slate/lib/tracking";
import type { InstanceOfComponent } from "@faire/slate/private/shared/types";
import { TabBase } from "@faire/slate/private/tab/TabBase";
import { ITabWebProps } from "@faire/slate/private/tab/types";
import React, { useRef } from "react";

type IProps<T extends string, C extends React.ElementType> = {
  /**
   * The accessory of the tab component. It can be a Tag or a Badge.
   */
  accessory?:
    | InstanceOfComponent<typeof Tag>
    | InstanceOfComponent<typeof Badge>;

  /**
   * The label of the tab component.
   */
  label: string;

  /**
   * Whether the tab component is selected.
   *
   * @default false
   */
  selected?: boolean;

  /**
   * **web**
   * Id of the tab panel that this tab controls.
   *
   * This is important for accessibility. Please set the `id` prop on the tab panel that this tab controls.
   */
  "aria-controls"?: string;

  /**
   * **web**
   * The underlying tab component.
   */
  as?: C;

  /**
   * **web**
   * Id of the tab. This is used to label the tab panel (through aria-labelledby).
   *
   * This is important for accessibility. Please make sure the tab panel is labelled either with `aria-labelledby`
   * referencing this `id` or with `aria-label` providing a label for the tab panel or any other label mechanism.
   */
  id?: string;

  /**
   * **web**
   * The tab value.
   */
  value: T;

  /**
   * **web**
   * The tab onSelect callback.
   *
   * @param value - The selected tab value.
   */
  onSelect?: (value: T) => void;
} & ITabWebProps<C>;

/**
 * A single tab component.
 *
 * Tabs organize content across different views or data sets.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Tab-2242efb5c25a801ca542d716289431df | Slate/Tab}
 */
export const Tab = <
  T extends string = string,
  C extends React.ElementType = "button",
>({
  accessory,
  as,
  label,
  selected = false,
  value,
  onClick,
  onSelect,
  ...props
}: IProps<T, C>) => {
  const ref = useRef<C>(null);
  const [{ badge, tag }, rest] = getSlots(accessory, {
    badge: Badge,
    tag: Tag,
  });

  // oxlint-disable-next-line no-explicit-any
  const handleClick = (e: React.MouseEvent<any>) => {
    onClick?.(e);
    onSelect?.(value);
  };

  // oxlint-disable-next-line no-explicit-any
  const handleKeyDown = (e: React.KeyboardEvent<any>) => {
    if (e.key === "Enter" || e.key === " ") {
      // oxlint-disable-next-line no-explicit-any
      if (ref.current && "click" in (ref.current as any)) {
        // oxlint-disable-next-line no-explicit-any
        (ref.current as any).click();
      }
    }
    props.onKeyDown?.(e);
  };

  if ([tag, badge].filter(Boolean).length > 1) {
    // oxlint-disable-next-line no-console
    console.error("Only one accessory is allowed");
  }

  if (rest.length > 0) {
    // oxlint-disable-next-line no-console
    console.error("Accessory accepts only Tag or Badge");
  }

  return (
    <TabBase
      className={componentCls()}
      data-component-name="Slate/Tab"
      onClick={handleClick}
      selected={selected}
      as={as as React.ElementType}
      ref={mergeRefs(ref, props.ref)}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <span>{label}</span>
      {badge}
      {tag ? React.cloneElement(tag, { onDismiss: undefined }) : null}
    </TabBase>
  );
};
